import React, { useState, useEffect, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const SELECTED_FILL = "#1d447d";   // dark blue
const UNSELECTED_FILL = "#9cb8da"; // light blue

const ClickableColumn = (props) => {
  const { x, y, width, height, payload, index, selected, background,
          horizontal, onBarMouseDown, onBarMouseEnter } = props;
  const fill = selected ? SELECTED_FILL : UNSELECTED_FILL;

  // hit area spans the full plot in the categorical direction
  let hit;
  if (horizontal) {
    const left = background ? background.x : 0;
    const full = background ? background.width : width;
    hit = { x: left, y, width: full, height };   // full row width
  } else {
    const top = background ? background.y : 0;
    const full = background ? background.height : height;
    hit = { x, y: top, width, height: full };     // full column height
  }

  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={fill} />
      <rect
        x={hit.x} y={hit.y} width={hit.width} height={hit.height}
        fill="rgba(0,0,0,0)"
        style={{ cursor: "pointer" }}
        onMouseDown={() => onBarMouseDown(index)}
        onMouseEnter={() => onBarMouseEnter(index)}
      />
    </g>
  );
};

function formatXAxisLabel(d) {
  if (/^\d{4}$/.test(String(d))) return String(d);
  if (/^\d{4}-\d{2}$/.test(String(d))) {
    const [y, m] = String(d).split("-");
    const monthName = new Date(Date.UTC(parseInt(y), parseInt(m) - 1)).toLocaleString("en-US", { month: "short" });
    return `${monthName} ${y}`;
  }
  return String(d);
}
function formatTooltipLabel(d) {
  return formatXAxisLabel(d); // same logic; collapsed
}

export function DateHistogram({ data, onSelectionChange, onOrientationChange, initialSelectedDates, query }) {
  const [selected, setSelected] = useState(() => new Set(initialSelectedDates || []));
  const [dragStart, setDragStart] = useState(null);
  const [dragCurrent, setDragCurrent] = useState(null);
  const [horizontal, setHorizontal] = useState(false);

  const dragging = dragStart !== null;

  // bars highlighted during an in-progress drag
  const preview = useMemo(() => {
    if (!dragging) return null;
    return { lo: Math.min(dragStart, dragCurrent), hi: Math.max(dragStart, dragCurrent) };
  }, [dragging, dragStart, dragCurrent]);

  const isSelected = (index, date) => {
    if (preview && index >= preview.lo && index <= preview.hi) return true;
    return selected.has(date);
  };

  const handleBarMouseDown = (index) => { setDragStart(index); setDragCurrent(index); };
  const handleBarMouseEnter = (index) => { if (dragging) setDragCurrent(index); };

  // seed visual selection from era-derived dates (visual only; never notifies)
  useEffect(() => {
    setSelected(new Set(initialSelectedDates || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(initialSelectedDates || []).join(",")]);

  // commit on mouseup anywhere; report the selection ONLY here, so the parent
  // is notified on a real gesture and never on mount or a data refresh.
  useEffect(() => {
    if (!dragging) return;
    const onUp = () => {
      const lo = Math.min(dragStart, dragCurrent);
      const hi = Math.max(dragStart, dragCurrent);

      const next = new Set(selected);
      if (lo === hi) {
        // no movement → treat as a click → toggle
        const d = data[lo]?.date;
        if (d != null) next.has(d) ? next.delete(d) : next.add(d);
      } else {
        // dragged across a range → add all bars in it
        for (let i = lo; i <= hi; i++) {
          const d = data[i]?.date;
          if (d != null) next.add(d);
        }
      }

      setSelected(next);
      onSelectionChange && onSelectionChange(data.filter((d) => next.has(d.date)));

      setDragStart(null);
      setDragCurrent(null);
    };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, [dragging, dragStart, dragCurrent, data, selected, onSelectionChange]);

  return (
    <div className="histogramContainer" style={{ userSelect: "none" }}>
      <div style={{ textAlign: "right", marginBottom: 4 }}>
      </div>
      <div className="histogramTitle">{query.length > 30 ? `${query.substring(0, 30)}...` : query}</div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart
          data={data}
          layout={horizontal ? "vertical" : "horizontal"}
          margin={{ top: 10, right: 15, left: 5, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          {horizontal ? (
            <>
              <XAxis type="number" tick={{ fontSize: 12 }}
               label={{ value: "Count", position: "insideBottom", offset: -10, fontSize: 14 }} />
              <YAxis type="category" dataKey="date" tick={{ fontSize: 12 }}
                tickFormatter={formatXAxisLabel} width={70} />
            </>
          ) : (
            <>
              <XAxis type="category" dataKey="date" tick={{ fontSize: 12 }}
                tickFormatter={formatXAxisLabel}
                label={{ value: "Date", position: "insideBottom", offset: -10, fontSize: 14 }} />
              <YAxis type="number" tick={{ fontSize: 12 }}
                label={{ value: "Count", offset: 20, angle: -90, position: "insideLeft", fontSize: 14, style: { textAnchor: "middle" } }} />
            </>
          )}
          <Tooltip labelFormatter={formatTooltipLabel} formatter={(value) => [`${value}`, "Count"]} />
          <Bar
            dataKey="count"
            background={{ fill: "rgba(0,0,0,0)" }}
            shape={(props) => (
              <ClickableColumn
                {...props}
                horizontal={horizontal}
                selected={isSelected(props.index, props.payload.date)}
                onBarMouseDown={handleBarMouseDown}
                onBarMouseEnter={handleBarMouseEnter}
              />
            )}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
